export interface Persistence {
  getItem(key: string): string | null

  getItemWithVersion(key: string, version: number): string | null

  setItem(key: string, value: string): void

  setItemWithVersion(key: string, value: string, version: number): void

  removeItem(key: string): void

  subStorage(prefix: string): Persistence

  onChange(key: string, handler: PersistenceChangeHandler): void

  clear(): void
}

type PersistenceChangeHandler = (newValue: string | null) => void

export class NoOpPersistence implements Persistence {
  getItem(_key: string) {
    return null
  }

  getItemWithVersion(_key: string, _version: number) {
    return null
  }

  setItem(_key: string, _value: string) {

  }

  setItemWithVersion(_key: string, _value: string, _version: number) {

  }

  removeItem(_key: string) {
  }

  subStorage(_prefix: string): Persistence {
    return this
  }

  onChange(_key: string, _handler: PersistenceChangeHandler) {
  }

  clear() {

  }
}

interface PersistedValue {
  version: number
  value: string
}

export class LocalStoragePersistence implements Persistence {
  private handlers: Record<string, PersistenceChangeHandler[]> = {}
  private readonly prefix: string

  constructor(keyPrefix: string, globalVersion: number = 0) {
    this.prefix = keyPrefix + (globalVersion > 1 ? `.v${globalVersion}` : "") + "_"
    window.addEventListener("storage", this.onStorageChange)
  }

  getItem(key: string) {
    return this.getItemWithVersion(key, 0)
  }

  getItemWithVersion(key: string, version: number) {
    const raw = localStorage.getItem(this.prefix + key)
    if (!raw) return null

    try {
      const parsed: PersistedValue = JSON.parse(raw)
      if (parsed.version !== version) {
        this.removeItem(key)
        return null
      }
      return parsed.value
    } catch {
      return null
    }
  }

  setItem(key: string, value: string) {
    this.setItemWithVersion(key, value, 0)
  }

  setItemWithVersion(key: string, value: string, version: number) {
    const wrapped: PersistedValue = { version, value }
    localStorage.setItem(this.prefix + key, JSON.stringify(wrapped))
  }

  removeItem(key: string) {
    localStorage.removeItem(this.prefix + key)
  }

  subStorage(prefix: string) {
    return new LocalStoragePersistence(this.prefix + prefix)
  }

  onChange(key: string, handler: PersistenceChangeHandler) {
    const fullKey = this.prefix + key
    const keyHandlers = this.handlers[fullKey] || (this.handlers[fullKey] = [])
    keyHandlers.push(handler)
  }

  clear() {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key && key.startsWith(this.prefix)) {
        localStorage.removeItem(key)
      }
    }
  }

  private onStorageChange = (event: StorageEvent) => {
    if (!event.key) return
    const handlers = this.handlers[event.key]
    if (!handlers) return

    try {
      const parsed: PersistedValue = JSON.parse(event.newValue ?? "null")
      handlers.forEach(handler => handler(parsed?.value ?? null))
    } catch {
      handlers.forEach(handler => handler(null))
    }
  }
}
